import React, { useState, useCallback } from 'react';
import { css } from 'aphrodite/no-important';
import { IconButton, IconSearch } from '@trainline/depot-web';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  DatePicker,
  DateChangeEventHandler,
} from '@trainline-shareable/journey-search/lib/primitives/DateTimePicker';
import dayjs from 'dayjs';

import { Modal, ModalHeader, ModalBody, ModalFooter } from '@contactcentre-web/common/Modal';
import Button from '@contactcentre-web/common/Button';

import styles from './styles';
import messages from './messages';

interface Props {
  datePickerStartDate: string;
  onCustomDateRangeSet: (selectedDate?: string) => void;
}

const CustomDateRange = ({ datePickerStartDate, onCustomDateRangeSet }: Props) => {
  const [selectedDate, setSelectedDate] = useState<string>();
  const [modalVisible, setModalVisible] = useState(false);
  const { formatMessage } = useIntl();

  const datePickerIntlMessages = {
    'Calendar.title.ariaLabel': formatMessage(messages.calendarTitleAriaLabel),
    'Calendar.previousMonthButton.ariaLabel': formatMessage(
      messages.calendarPreviousMonthButtonAriaLabel
    ),
    'Calendar.nextMonthButton.ariaLabel': formatMessage(messages.calendarNextMonthButtonAriaLabel),
    'Calendar.DateButton.ariaLabel': (date: string) =>
      formatMessage(messages.calendarDateButtonAriaLabel, {
        date: date,
      }),
    'Calendar.initialInstructions.ariaLabel': formatMessage(
      messages.calendarInitialInstructionsAriaLabel
    ),
  };

  const onDateChange: DateChangeEventHandler = useCallback((_, date: string) => {
    setSelectedDate(date);
  }, []);

  const onSearchDate = useCallback(() => {
    onCustomDateRangeSet(selectedDate || datePickerStartDate);
    setSelectedDate('');
    setModalVisible(false);
  }, [selectedDate, datePickerStartDate, onCustomDateRangeSet]);

  const today = dayjs(new Date()).format('D MMM YYYY');

  return (
    <div>
      <IconButton icon={IconSearch} contained onClick={() => setModalVisible(true)} />
      {modalVisible && (
        <Modal onClose={() => setModalVisible(false)}>
          <ModalHeader />
          <ModalBody>
            <DatePicker
              intlMessages={datePickerIntlMessages}
              value={datePickerStartDate}
              onChange={onDateChange}
              locale="en-GB"
              max={today}
            />
          </ModalBody>
          <ModalFooter>
            <div className={css(styles.buttonContainer)}>
              <Button size="medium" variant="secondary" fullWidth onClick={onSearchDate}>
                <FormattedMessage {...messages.searchDate} />
              </Button>
            </div>
          </ModalFooter>
        </Modal>
      )}
    </div>
  );
};

export default CustomDateRange;
